import { api, myBox } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { empty, field, idRef, meter, pageHead, pill } from '../console/components.ts';
import { ApiError } from '../console/errors.ts';
import {
  agentName,
  date,
  esc,
  FREQUENCY,
  fmt,
  icon,
  iso,
  lineName,
  type Money,
  person,
  when,
} from '../console/format.ts';
import { followsClaims, followsRenewals, has } from '../console/session.ts';
import {
  type EffortView,
  effortsHtml,
  type PaymentView,
  paymentCells,
  type ReceiptView,
  receiptDetails,
} from './cobranza.ts';
import {
  type CancellationRequestView,
  cancelPanel,
  endorsementOptions,
  endorsePanel,
  historyHtml,
  type PolicyHistoryView,
  pendingRequest,
} from './poliza-acciones.ts';
import { followUpHtml, type NoteRow, type TaskRow } from './seguimiento.ts';
import { type ClaimRow, policyClaimsHtml } from './siniestros.ts';

/**
 * The policy's own page (#310): a header with what identifies it, how it stands and its
 * indicators, its actions (collect, renew, endorse, documents, consent; cancel apart), and tabs
 * — Resumen, Riesgo y coberturas, Prima y recibos, Comisión (whoever reads commissions),
 * Siniestros (whoever follows claims, #309), Historial and Documentos. Reached from «Pólizas y recibos» (`polizas?poliza=<id>`), from the
 * customer's page and from a notice; the buttons it shows are answered by the policies' view.
 */

type Holder = {
  fullName: string;
  nationalId: string | null;
  nationalIdMasked?: boolean;
  taxId?: string | null;
  email?: string | null;
  phone?: string | null;
  politicallyExposed?: boolean | null;
};

/** A policy as GET /v1/policies lists it (ADR-0030, #310). */
export type PolicySummary = {
  id: string;
  scheduleId: string;
  carrier: string;
  productLine: string;
  policyNumber: string;
  plan: { code: string; name: string | null } | null;
  status: string;
  collection: string | null;
  period: { start: string; end: string | null };
  daysLeft: number | null;
  currency: string;
  annualPremium: Money | null;
  paymentFrequency: string | null;
  installmentCount: number | null;
  collectedBy: 'AGENCY' | 'CARRIER';
  policyholder: Holder | null;
  agentId: string;
  nextInstallment: { number: number; dueDate: string; amount: Money } | null;
  receipts: { total: number; paid: number; overdue: number; pending: number } | null;
};

type Coverage = {
  code: string | null;
  carrierCode: string;
  name: string | null;
  limit: Money | null;
  deductible: Money | null;
  coinsurance: string | null;
  simulated: boolean;
};
type Vehicle = {
  make: string;
  model: string;
  modelYear: number;
  plate: string | null;
  vin: string | null;
  color: string | null;
  use: string;
  insuredValue: Money | null;
};
type InsuredPerson = {
  name: string | null;
  relationship: string;
  birthDate: string | null;
  exclusions: string[];
};

/** GET /v1/policies/:id: everything that was contracted, its chain and its history. */
export type PolicyDetail = PolicySummary & {
  cancellation: PolicyHistoryView['cancellation'];
  lapsedOn: string | null;
  quoteId: string | null;
  carrierQuoteRef: string | null;
  issuedOn: string | null;
  firstEffectiveDate: string;
  previousId: string | null;
  renewedById: string | null;
  premium: {
    net: Money;
    issuanceFee: Money | null;
    installmentSurcharge: Money | null;
    vat: Money | null;
    total: Money;
  } | null;
  coverages: Coverage[] | null;
  risk:
    | { kind: 'VEHICLE'; vehicle: Vehicle }
    | { kind: 'PERSONS'; persons: InsuredPerson[] }
    | null;
  riskHistory?: { at: string; reference: string | null }[];
  beneficiaries: { fullName: string; relationship: string; sharePercent: number }[];
  beneficiaryHistory?: { at: string; by: string; reference: string | null }[];
  /** Its endorsements, cancellation requests and the one pending (#311). */
  endorsements?: PolicyHistoryView['endorsements'];
  cancellationRequests?: PolicyHistoryView['cancellationRequests'];
  cancellationRequest?: CancellationRequestView | null;
  /** Its open tasks and its notes, and whether this person writes them (#317). */
  openTasks?: TaskRow[];
  notes?: NoteRow[];
  canNote?: boolean;
};

/** GET /v1/schedules/:id: the receipts of the period. */
export type PolicyReceipts = {
  id: string;
  collectionModel: string;
  carrierSyncedOn?: string | null;
  installments: ReceiptView[];
};
type LinkView = {
  id: string;
  installment: number;
  amount: Money;
  createdAt: string;
  expiresAt: string;
  state: string;
};
/** GET /v1/policies/:id/commission (403 for whoever does not read commissions). */
export type PolicyCommission = {
  term: {
    rate: string;
    netPremium: Money;
    grossPremium: Money;
    annualCommission: Money;
  } | null;
  accruals: {
    id: string;
    kind: string;
    installment: number | null;
    date: string;
    base: Money;
    commission: Money;
    standing: string;
    disputed: boolean;
  }[];
  accrued: Money[];
};
type OperationView = {
  kind: 'ISSUE' | 'ENDORSE';
  carrierRef: string;
  option: string | null;
  state: 'PENDING' | 'DONE' | 'REJECTED';
  reason: string | null;
  createdAt: string;
  updatedAt: string;
};
type RenewalNotices = {
  scheduleId: string;
  notices: { kind: string; window: string; sentAt: string }[];
};

/** The policy's own status (ADR-0030): label and pill. */
export const POLICY_STATUS: Record<string, [label: string, pill: string]> = {
  IN_FORCE: ['Vigente', 'ok'],
  CANCELLED: ['Cancelada', 'CANCELLED'],
  EXPIRED: ['Vencida', 'warn'],
  RENEWED: ['Renovada', 'info'],
  LAPSED: ['Caducada', 'bad'],
};
/** How its receipts stand (billing's): label and pill. */
export const COLLECTION: Record<string, [label: string, pill: string]> = {
  CURRENT: ['Al día', 'ok'],
  OVERDUE: ['Recibo vencido', 'warn'],
  DELINQUENT: ['En mora', 'bad'],
  UNCONFIRMED: ['Sin confirmar', 'warn'],
  PAID: ['Pagada', 'ok'],
  CANCELLED: ['Recibos anulados', 'CANCELLED'],
};
/** The documents a carrier gives for a policy (GET /v1/carriers `documents`). */
export const DOCUMENT: Record<string, string> = {
  POLICY: 'Póliza',
  CERTIFICATE: 'Certificado',
  QUOTE: 'Cotización',
};
const RELATIONSHIP: Record<string, string> = {
  HOLDER: 'Titular',
  SPOUSE: 'Cónyuge',
  CHILD: 'Hijo o hija',
  PARENT: 'Padre o madre',
  SIBLING: 'Hermano o hermana',
  OTHER: 'Otro',
};
const USE: Record<string, string> = { PRIVATE: 'Particular', COMMERCIAL: 'Comercial' };
const METHOD: Record<string, string> = {
  CASH: 'Efectivo',
  TRANSFER: 'Transferencia',
  CARD: 'Tarjeta',
  PAYMENT_LINK: 'Enlace de pago',
};
const ACCRUAL: Record<string, string> = {
  COMMISSION: 'Comisión',
  CLAWBACK: 'Reversión',
  ADJUSTMENT: 'Ajuste por endoso',
};
const STANDING: Record<string, [label: string, pill: string]> = {
  ACCRUED: ['Devengada', 'info'],
  READY_TO_INVOICE: ['Lista para facturar', 'warn'],
  SETTLED: ['Liquidada', 'ok'],
};
const LINK: Record<string, [label: string, pill: string]> = {
  ACTIVE: ['Vigente', 'info'],
  EXPIRED: ['Vencido', 'warn'],
  PAID: ['Pagado', 'ok'],
  SUPERSEDED: ['Reemplazado', 'CANCELLED'],
};
const CANCEL_REASON: Record<string, string> = {
  CUSTOMER_REQUEST: 'a solicitud del cliente',
  RISK_TRANSFERRED: 'el riesgo se transfirió',
  NON_PAYMENT: 'por falta de pago',
};
const CURRENCY: Record<string, string> = { GTQ: 'Quetzales (GTQ)', USD: 'Dólares (USD)' };

/** The tabs, in order (Siniestros, #309: for who follows claims; Tareas y notas, #317). */
export const POLICY_TABS = [
  ['resumen', 'Resumen'],
  ['riesgo', 'Riesgo y coberturas'],
  ['prima', 'Prima y recibos'],
  ['comision', 'Comisión'],
  ['siniestros', 'Siniestros'],
  ['tareas', 'Tareas y notas'],
  ['historial', 'Historial'],
  ['documentos', 'Documentos'],
] as const;
export type PolicyTab = (typeof POLICY_TABS)[number][0];
export const isPolicyTab = (t: string | null | undefined): t is PolicyTab =>
  POLICY_TABS.some(([k]) => k === t);

/** «Plan Todo Riesgo» or its code; null without a plan. */
export const planName = (p: { plan: PolicySummary['plan'] }) =>
  p.plan ? (p.plan.name ?? p.plan.code) : null;

/** «Autos · Automóvil Todo Riesgo», or the product line alone. */
export const lineAndPlan = (p: Pick<PolicySummary, 'productLine' | 'plan'>) => {
  const plan = planName(p);
  return plan ? `${lineName(p.productLine)} · ${plan}` : lineName(p.productLine);
};

/** «faltan 344 días», «vence mañana», «venció hace 3 días». */
export function daysText(n: number | null): string {
  if (n === null) return 'sin fecha de fin';
  if (n > 1) return `faltan ${n} días`;
  if (n === 1) return 'vence mañana';
  if (n === 0) return 'vence hoy';
  return `venció hace ${-n} ${n === -1 ? 'día' : 'días'}`;
}

/** Σ per currency (quetzales and dollars are never added together). */
export function totals(ms: (Money | null | undefined)[]): Money[] {
  const by = new Map<string, number>();
  for (const m of ms) if (m) by.set(m.currency, (by.get(m.currency) ?? 0) + m.amountMinor);
  return (['GTQ', 'USD'] as const)
    .filter((c) => by.has(c))
    .map((c) => ({ amountMinor: by.get(c) ?? 0, currency: c }));
}
const moneyList = (ms: Money[], none = '—') => (ms.length ? ms.map(fmt).join(' · ') : none);

const statusPill = (s: string) => {
  const [label, tone] = POLICY_STATUS[s] ?? [s, 'info'];
  return pill(tone, label);
};
export const collectionPill = (c: string | null) => {
  if (!c) return '';
  const [label, tone] = COLLECTION[c] ?? [c, 'info'];
  return pill(tone, label);
};

/** The term pair of a definition list; a missing value reads «—» (or the text given). */
const dl = (rows: [string, string | null | undefined][]) =>
  `<dl class="party-data policy-data">${rows
    .map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${v ?? '<span class="muted">—</span>'}</dd></div>`)
    .join('')}</dl>`;

/** Who sees the commissions of a policy (settlement.read: management and the agent). */
export const readsCommissions = () =>
  has('agency_director', 'agency_admin', 'agency_manager', 'agent');

/** Everything the page shows, read at once (one request per source, never per row). */
export type PolicyPageData = {
  policy: PolicyDetail;
  receipts: PolicyReceipts;
  payments: PaymentView[];
  /** The collections log (#315). */
  efforts: EffortView[];
  links: LinkView[];
  commission: PolicyCommission | null;
  operations: OperationView[];
  carriers: CarrierView[];
  notices: RenewalNotices['notices'];
  cashBox: string | null;
  /** The policy's claims (null: the person does not follow claims). */
  claims?: ClaimRow[] | null;
};

/** A 403 means «not yours to see»: the tab is not shown; anything else is an error. */
const forbiddenAsNull = (e: unknown) => {
  if (e instanceof ApiError && e.status === 403) return null;
  throw e;
};

export async function loadPolicyPage(id: string): Promise<PolicyPageData> {
  const policy = await api<PolicyDetail>('GET', `/v1/policies/${encodeURIComponent(id)}`);
  const sid = encodeURIComponent(policy.scheduleId);
  const [receipts, payments, efforts, links, commission, operations, carriers, renewals, box] =
    await Promise.all([
      api<PolicyReceipts>('GET', `/v1/schedules/${sid}`),
      api<PaymentView[]>('GET', `/v1/schedules/${sid}/payments`),
      api<EffortView[]>('GET', `/v1/schedules/${sid}/collection-efforts`).catch(
        () => [] as EffortView[],
      ),
      api<LinkView[]>('GET', `/v1/schedules/${sid}/payment-links`).catch(() => [] as LinkView[]),
      readsCommissions()
        ? api<PolicyCommission>(
            'GET',
            `/v1/policies/${encodeURIComponent(policy.id)}/commission`,
          ).catch(forbiddenAsNull)
        : Promise.resolve(null),
      api<OperationView[]>('GET', `/v1/carrier-operations?scheduleId=${sid}`).catch(
        () => [] as OperationView[],
      ),
      api<CarrierView[]>('GET', '/v1/carriers'),
      api<RenewalNotices[]>('GET', '/v1/renewals').catch(() => [] as RenewalNotices[]),
      myBox(),
    ]);
  const claims = followsClaims()
    ? await api<{ items: ClaimRow[] }>(
        'GET',
        `/v1/claims?policyId=${encodeURIComponent(policy.id)}`,
      )
        .then((r) => r.items)
        .catch(() => null)
    : null;
  return {
    policy,
    receipts,
    payments,
    efforts,
    links,
    commission,
    operations,
    carriers,
    notices: renewals.find((r) => r.scheduleId === policy.scheduleId)?.notices ?? [],
    cashBox: box?.state === 'OPEN' ? box.id : null,
    claims,
  };
}

/** The receipt to collect next: the oldest unpaid one by due date. */
const nextReceipt = (r: PolicyReceipts) =>
  r.installments
    .filter((i) => i.status !== 'PAID' && i.status !== 'CANCELLED')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.number - b.number)[0];

/**
 * What is still owed (saldo): the receipts neither paid nor cancelled, less what a carrier that
 * takes part payments already received of one (per currency).
 */
export const balanceOf = (r: PolicyReceipts) =>
  totals(
    r.installments
      .filter((i) => i.status !== 'PAID' && i.status !== 'CANCELLED')
      .map((i) =>
        i.partiallyPaid
          ? { ...i.amount, amountMinor: i.amount.amountMinor - i.partiallyPaid.amountMinor }
          : i.amount,
      ),
  );

// ------------------------------------------------------------------ header and actions

/** The actions of the policy (the policies' view answers them): «Más acciones», cancel apart. */
function actionsBar(d: PolicyPageData) {
  const { policy: p, receipts: r, carriers } = d;
  const carrier = carriers.find((c) => c.id === p.carrier);
  const cancelled = r.installments.some((i) => i.status === 'CANCELLED');
  const canCancel = has('agency_director', 'agency_admin');
  // The policy's own agent asks the direction to cancel it (#311).
  const asksToCancel = has('agent') && !canCancel;
  const changes = canCancel && !cancelled ? endorsementOptions(carrier, p.productLine) : [];
  const pending = cancelled ? null : (p.cancellationRequest ?? null);
  const ref = {
    id: p.scheduleId,
    carrier: p.carrier,
    productLine: p.productLine,
    policyNumber: p.policyNumber,
    currency: r.installments[0]?.amount.currency ?? p.currency,
  };
  const renewable =
    !cancelled &&
    !p.renewedById &&
    p.status !== 'RENEWED' &&
    has('agency_director', 'agency_admin', 'agent') &&
    !!carrier?.renew;
  const recordsConsent = has('agency_director', 'agency_admin', 'agent');
  const readsParty = has('agency_director', 'agency_admin', 'agency_manager', 'agent');
  const holder = p.policyholder;
  const more = [
    holder && readsParty
      ? `<button type="button" class="menu-item" data-party-of-schedule="${esc(p.scheduleId)}" title="Ver la ficha de ${esc(holder.fullName)}">${icon('users')}Ver al cliente</button>`
      : '',
    ...documentsOf(d).map(
      (k) =>
        `<button type="button" class="menu-item" data-policy-doc="${esc(p.scheduleId)}" data-kind="${esc(k)}" data-number="${esc(p.policyNumber)}" aria-label="Descargar ${esc(DOCUMENT[k]?.toLowerCase() ?? k)} de la póliza ${esc(p.policyNumber)} en PDF">${icon('download')}${esc(DOCUMENT[k] ?? k)}</button>`,
    ),
    renewable
      ? `<button type="button" class="menu-item" data-renew-open="${esc(p.scheduleId)}" aria-expanded="false">${icon('trend')}Renovar</button>`
      : '',
    changes.length
      ? `<button type="button" class="menu-item" data-endorse-open="${esc(p.scheduleId)}" aria-expanded="false">${icon('file')}Endosar</button>`
      : '',
    recordsConsent && holder
      ? `<button type="button" class="menu-item" data-party-of-schedule="${esc(p.scheduleId)}" data-consent="1" title="Se registra en la ficha del cliente">${icon('bell')}Consentimiento de mensajes</button>`
      : '',
    // In the page the history is a tab of its own (#311's «Historial de endosos y cancelación»),
    // for whoever endorses or cancels.
    canCancel || asksToCancel
      ? `<button type="button" class="menu-item" data-policy-goto-tab="historial">${icon('layers')}Historial de endosos y cancelación</button>`
      : '',
  ].filter(Boolean);
  const start = r.installments[0]?.dueDate ?? p.period.start;
  const cancel =
    cancelled || pending
      ? ''
      : canCancel
        ? `<button type="button" class="btn danger policy-cancel" data-cancel="${esc(p.scheduleId)}" aria-expanded="false">Cancelar póliza</button>`
        : asksToCancel
          ? `<button type="button" class="btn policy-cancel-request" data-cancel-request-open="${esc(p.scheduleId)}" aria-expanded="false">Solicitar cancelación</button>`
          : '';
  const today = iso(new Date());
  const effective = start > today ? start : today;
  const bar =
    more.length || cancel
      ? `<div class="policy-actions">${
          more.length
            ? `<details class="more-actions" data-more-actions><summary class="btn">Más acciones${icon('chevron', 'icon chevron')}</summary><div class="menu" role="group" aria-label="Más acciones de la póliza ${esc(p.policyNumber)}">${more.join('')}</div></details>`
            : ''
        }<span class="spacer"></span>${cancel}</div>`
      : '';
  const renew = renewable
    ? `<div class="renew-panel" data-renew-panel="${esc(p.scheduleId)}" hidden></div>`
    : '';
  const endorse = changes.length ? endorsePanel(ref, changes, effective) : '';
  const cancelling = cancel ? cancelPanel(ref, canCancel ? 'CANCEL' : 'REQUEST', effective) : '';
  return `${bar}${pending ? pendingRequest(pending, ref, canCancel) : ''}${renew}${endorse}${cancelling}`;
}

/** The documents its carrier gives (management, the manager and the policy's own agent). */
const documentsOf = (d: PolicyPageData) =>
  has('agency_director', 'agency_admin', 'agency_manager', 'agent')
    ? (d.carriers.find((c) => c.id === d.policy.carrier)?.documents ?? [])
    : [];

const kpi = (label: string, iconName: string, value: string, foot: string, tone = '') =>
  `<div class="card kpi ${tone}"><span class="kpi-label">${icon(iconName)}${esc(label)}</span><span class="kpi-value">${value}</span><span class="kpi-foot">${foot}</span></div>`;

function header(d: PolicyPageData) {
  const { policy: p, receipts: r } = d;
  const paid = r.installments.filter((i) => i.status === 'PAID').length;
  const late = r.installments.filter(
    (i) => !i.unconfirmed && (i.status === 'OVERDUE' || i.status === 'DELINQUENT'),
  ).length;
  const pending = r.installments.filter(
    (i) => i.status !== 'PAID' && i.status !== 'CANCELLED',
  ).length;
  const cancelled = r.installments.some((i) => i.status === 'CANCELLED');
  const next = nextReceipt(r);
  const byAgency = r.collectionModel === 'AGENCY';
  const term = d.commission?.term;
  const endorsements = p.endorsements?.length ?? 0;
  const unconfirmed = r.installments.some((i) => i.unconfirmed);
  const plan = planName(p);
  return `${pageHead(
    p.policyNumber,
    `${esc(carrierName(p.carrier))} · ${esc(lineName(p.productLine))}${plan ? ` · ${esc(plan)}` : ''}`,
    '<button type="button" class="btn" data-policy-back>← Pólizas y recibos</button>',
  )}
  <section class="card policy-summary" aria-label="La póliza de un vistazo">
    <div class="policy-head">
      <div class="policy-states">${statusPill(p.status)}${collectionPill(p.collection)}</div>
      <div class="policy-meta">
        <span data-policy-period>Vigencia <b>${esc(date(p.period.start))} – ${p.period.end ? esc(date(p.period.end)) : 'sin fecha de fin'}</b> · ${esc(daysText(p.daysLeft))}</span>
        <span>Agente <b>${esc(agentName(p.agentId))}</b></span>
        <span>Cobra <b>${byAgency ? 'la agencia' : 'la aseguradora'}</b></span>
      </div>
      ${cancelled ? '' : `<span class="progress-text">${meter(r.installments.length ? paid / r.installments.length : 0, 'inline')}${paid} de ${r.installments.length} pagados</span>`}
    </div>
    ${actionsBar(d)}
    ${unconfirmed ? `<p class="note">${icon('warn')}<span>Los recibos pendientes están sin confirmar con la aseguradora: ${r.carrierSyncedOn ? `Nubi los leyó el ${esc(date(r.carrierSyncedOn))}` : 'la aseguradora aún no los confirma'}. Puede que el cliente ya los haya pagado.</span></p>` : ''}
  </section>
  <section class="kpis policy-kpis" aria-label="Indicadores de la póliza">
    ${kpi('Recibos pendientes', 'receipt', String(pending), next ? `el próximo vence el ${esc(date(next.dueDate))}` : 'Ninguno por cobrar')}
    ${kpi('Recibos vencidos', 'warn', String(late), late ? 'Por cobrar ya' : 'Ninguno vencido', late ? 'warn' : '')}
    ${kpi('Recibos pagados', 'ok', String(paid), `de ${r.installments.length}`)}
    ${term ? kpi('Comisión del período', 'settle', fmt(term.annualCommission), `tasa ${esc(term.rate)} %`) : ''}
    ${endorsements ? kpi('Endosos', 'file', String(endorsements), 'en esta vigencia') : ''}
  </section>`;
}

// ------------------------------------------------------------------ tabs

function summaryTab(d: PolicyPageData) {
  const p = d.policy;
  const h = p.policyholder;
  const readsParty = has('agency_director', 'agency_admin', 'agency_manager', 'agent');
  const pep = h?.politicallyExposed === true ? 'Sí' : h?.politicallyExposed === false ? 'No' : null;
  const holder = h
    ? `${dl([
        ['Nombre', `<strong>${esc(h.fullName)}</strong>`],
        ['DPI', h.nationalId ? `<span class="mono">${esc(h.nationalId)}</span>` : null],
        ['NIT', h.taxId ? `<span class="mono">${esc(h.taxId)}</span>` : null],
        ['Teléfono', h.phone ? esc(h.phone) : null],
        ['Correo', h.email ? esc(h.email) : null],
        ['Persona expuesta políticamente (PEP)', pep],
      ])}${h.nationalIdMasked ? '<p class="muted small policy-note">El DPI completo lo ven el agente de la póliza y la administración.</p>' : ''}`
    : '<p class="muted policy-note">Esta póliza se emitió antes de que Nubi guardara a su contratante.</p>';
  const chainLink = (id: string, label: string) =>
    `<button type="button" class="btn sm" data-policy-open="${esc(id)}">${esc(label)}</button>`;
  return `<div class="policy-grid">
    <section class="card"><div class="card-head"><h3>Contratante</h3><span class="spacer"></span>${
      h && readsParty
        ? `<button type="button" class="btn sm" data-party-of-schedule="${esc(p.scheduleId)}">${icon('users')}Ver su ficha</button>`
        : ''
    }</div>${holder}</section>
    <section class="card"><div class="card-head"><h3>La póliza</h3></div>${dl([
      ['Cotización de origen', p.quoteId ? idRef(p.quoteId) : null],
      [
        'Referencia de la aseguradora',
        p.carrierQuoteRef ? `<span class="mono">${esc(p.carrierQuoteRef)}</span>` : null,
      ],
      [
        'Póliza anterior',
        p.previousId
          ? chainLink(p.previousId, 'Ver la anterior')
          : 'Ninguna: es la primera vigencia',
      ],
      [
        'Renovada por',
        p.renewedById
          ? chainLink(p.renewedById, 'Ver la renovación')
          : // Its renewal is worked in «Renovaciones» (#308): who follows it, the offer, the proposal.
            `Todavía no se renueva${
              (p.status === 'IN_FORCE' || p.status === 'EXPIRED') && followsRenewals()
                ? ` <button type="button" class="btn sm" data-go="renovaciones?renovar=${esc(p.id)}">${icon('trend')}Gestionar la renovación</button>`
                : ''
            }`,
      ],
      ['Primer efecto', esc(date(p.firstEffectiveDate))],
      ['Fecha de emisión', p.issuedOn ? esc(date(p.issuedOn)) : null],
      ['Moneda', esc(CURRENCY[p.currency] ?? p.currency)],
      [
        'Cancelación',
        p.cancellation
          ? `${p.cancellation.effectiveDate ? `desde el ${esc(date(p.cancellation.effectiveDate))}` : 'sin fecha registrada'}${p.cancellation.reason ? `, ${esc(CANCEL_REASON[p.cancellation.reason] ?? p.cancellation.reason)}` : ''}`
          : 'No está cancelada',
      ],
    ])}</section></div>`;
}

const moneyOr = (m: Money | null | undefined, none = '<span class="muted">No indica</span>') =>
  m ? fmt(m) : none;

function riskTab(d: PolicyPageData) {
  const p = d.policy;
  const risk = p.risk;
  const riskHtml = !risk
    ? '<p class="muted policy-note">Nubi no guardó el riesgo asegurado de esta póliza.</p>'
    : risk.kind === 'VEHICLE'
      ? dl([
          [
            'Vehículo',
            `<strong>${esc(risk.vehicle.make)} ${esc(risk.vehicle.model)} ${esc(risk.vehicle.modelYear)}</strong>`,
          ],
          [
            'Placa',
            risk.vehicle.plate ? `<span class="mono">${esc(risk.vehicle.plate)}</span>` : null,
          ],
          [
            'Chasis',
            risk.vehicle.vin ? `<span class="mono">${esc(risk.vehicle.vin)}</span>` : null,
          ],
          ['Color', risk.vehicle.color ? esc(risk.vehicle.color) : null],
          ['Uso', esc(USE[risk.vehicle.use] ?? risk.vehicle.use)],
          ['Valor asegurado', risk.vehicle.insuredValue ? fmt(risk.vehicle.insuredValue) : null],
        ])
      : risk.persons.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Asegurado</th><th>Parentesco</th><th>Nacimiento</th><th>Exclusiones</th></tr></thead><tbody>${risk.persons
            .map(
              (x) =>
                `<tr><td>${x.name ? esc(x.name) : '<span class="muted">Sin nombre</span>'}</td><td>${esc(RELATIONSHIP[x.relationship] ?? x.relationship)}</td><td class="nowrap">${x.birthDate ? esc(date(x.birthDate)) : '—'}</td><td>${x.exclusions.length ? esc(x.exclusions.join('; ')) : '<span class="muted">Ninguna</span>'}</td></tr>`,
            )
            .join('')}</tbody></table></div>`
        : '<p class="muted policy-note">La póliza no tiene personas aseguradas registradas.</p>';
  const coverages = p.coverages ?? [];
  const simulated = coverages.some((c) => c.simulated);
  const coverageHtml = coverages.length
    ? `<div class="table-wrap" data-component="table"><table class="table coverages"><thead><tr><th>Cobertura</th><th class="num">Suma asegurada o límite</th><th class="num">Deducible</th><th class="num">Coaseguro</th></tr></thead><tbody>${coverages
        .map(
          (c) =>
            `<tr data-coverage="${esc(c.carrierCode)}"><td>${esc(c.name ?? c.carrierCode)}${c.simulated ? ' <span class="chip" title="La aseguradora no publica esta cifra: es un parámetro del simulador, por confirmar">simulado</span>' : ''}</td><td class="num">${moneyOr(c.limit)}</td><td class="num">${moneyOr(c.deductible)}</td><td class="num">${c.coinsurance ? `${esc(c.coinsurance)} %` : '<span class="muted">No indica</span>'}</td></tr>`,
        )
        .join(
          '',
        )}</tbody></table></div>${simulated ? '<p class="muted small policy-note">«simulado»: la aseguradora no publica esa cifra; la confirma al emitir en producción.</p>' : ''}`
    : '<p class="muted policy-note">Nubi no guardó las coberturas de esta póliza: se emitió sin una cotización guardada.</p>';
  const beneficiaries = p.beneficiaries.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Beneficiario</th><th>Parentesco</th><th class="num">Porcentaje</th></tr></thead><tbody>${p.beneficiaries
        .map(
          (b) =>
            `<tr><td>${esc(b.fullName)}</td><td>${esc(RELATIONSHIP[b.relationship] ?? b.relationship)}</td><td class="num">${b.sharePercent} %</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : '<p class="muted policy-note">Sin beneficiarios designados.</p>';
  return `<section class="card"><div class="card-head"><h3>${risk?.kind === 'PERSONS' ? 'Personas aseguradas' : 'Vehículo asegurado'}</h3></div>${riskHtml}</section>
    <section class="card"><div class="card-head"><h3>Coberturas</h3><span class="spacer"></span><span class="muted small">${coverages.length} ${coverages.length === 1 ? 'cobertura' : 'coberturas'}</span></div>${coverageHtml}</section>
    <section class="card"><div class="card-head"><h3>Beneficiarios</h3></div>${beneficiaries}</section>`;
}

function premiumTab(d: PolicyPageData) {
  const { policy: p, receipts: r } = d;
  const pr = p.premium;
  const breakdown = pr
    ? `<div class="table-wrap" data-component="table"><table class="table breakdown"><tbody>
        <tr><th scope="row">Prima neta</th><td class="num">${fmt(pr.net)}</td></tr>
        <tr><th scope="row">Gastos de emisión</th><td class="num">${moneyOr(pr.issuanceFee)}</td></tr>
        <tr><th scope="row">Recargo por fraccionamiento</th><td class="num">${moneyOr(pr.installmentSurcharge)}</td></tr>
        <tr><th scope="row">IVA</th><td class="num">${moneyOr(pr.vat)}</td></tr>
        <tr class="total"><th scope="row">Total</th><td class="num">${fmt(pr.total)}</td></tr></tbody></table></div>`
    : '<p class="muted policy-note">Nubi no guardó el desglose de la prima de esta póliza.</p>';
  const frequency = p.paymentFrequency
    ? `${esc(FREQUENCY[p.paymentFrequency] ?? p.paymentFrequency)}${p.installmentCount ? ` · ${p.installmentCount} ${p.installmentCount === 1 ? 'cuota' : 'cuotas'}` : ''}`
    : p.installmentCount
      ? `${p.installmentCount} cuotas`
      : '<span class="muted">Sin dato</span>';
  const next = nextReceipt(r);
  const byAgency = r.collectionModel === 'AGENCY';
  const canCollect = has('agency_director', 'agency_admin', 'cashier', 'agent');
  const n = r.installments.length;
  const sorted = [...r.installments].sort((a, b) => a.number - b.number);
  const rows = sorted
    .map((i, k) => {
      // The days it covers as the carrier billed it; before #315, up to the next receipt's.
      const from = i.coversFrom ?? i.dueDate;
      const until = i.coversTo ?? sorted[k + 1]?.dueDate ?? p.period.end;
      const x = receiptDetails(i);
      return `<tr><td class="mono r-n">${i.number}/${n}</td><td class="r-period"><span class="nowrap">${esc(date(from))}${until ? ` – ${esc(date(until))}` : ''}</span>${x.kind}${x.number}${x.breakdown}</td><td class="nowrap r-due">${esc(date(i.dueDate))}</td>
        <td class="num r-amount">${fmt(i.amount)}${i.endorsementRef && !i.kind ? '<div class="muted small">endoso</div>' : ''}</td><td class="r-state">${i.unconfirmed ? pill('warn', 'Sin confirmar') : pill(i.status)}${x.state}</td><td class="nowrap r-paid">${i.paidOn ? esc(date(i.paidOn)) : '<span class="muted">—</span>'}${x.paid}</td>
        <td class="actions">${next?.number === i.number && byAgency && canCollect ? `<button type="button" class="btn sm primary" data-pay="${esc(r.id)}" data-n="${i.number}" data-amount="${esc(JSON.stringify(i.amount))}">${icon('cash')}Transferencia ${fmt(i.amount)}</button>${d.cashBox ? ` <button type="button" class="btn sm" data-pay="${esc(r.id)}" data-n="${i.number}" data-method="CASH" data-box="${esc(d.cashBox)}" data-amount="${esc(JSON.stringify(i.amount))}">Efectivo</button>` : ''}${p.policyholder ? ` <button type="button" class="btn sm" data-pay-link="${esc(r.id)}" data-n="${i.number}" data-amount="${esc(JSON.stringify(i.amount))}" title="Se envía al contratante por el canal que aceptó">Enviar enlace de pago</button>` : ''}` : ''}</td></tr>`;
    })
    .join('');
  // What was paid: Nubi's payments, and the receipts the carrier collected (it registers them).
  const recorded = new Set(d.payments.map((x) => x.installment));
  const byCarrier = byAgency
    ? []
    : r.installments.filter((i) => i.status === 'PAID' && !recorded.has(i.number));
  const paymentRows = [
    ...d.payments.map((x) => {
      const c = paymentCells(x, byAgency);
      return {
        on: x.capturedOn,
        html: `<tr${x.reversal ? ' class="reversed"' : ''}><td class="nowrap">${esc(date(x.capturedOn))}</td><td class="mono">${x.installment}/${n}</td><td class="num">${fmt(x.amount)}</td><td data-payment-method>${esc(METHOD[x.method] ?? x.method)}</td><td class="mono small">${esc(x.reference)}</td><td>${esc(person(x.capturedBy))}</td><td data-payment-state>${c.state}</td><td class="actions">${c.actions}</td></tr>${c.panel}`,
      };
    }),
    ...byCarrier.map((i) => ({
      on: i.paidOn ?? i.dueDate,
      html: `<tr><td class="nowrap">${esc(date(i.paidOn ?? i.dueDate))}</td><td class="mono">${i.number}/${n}</td><td class="num">${fmt(i.amount)}</td><td data-payment-method>Cobro de la aseguradora</td><td class="muted small">—</td><td>${esc(carrierName(p.carrier))}</td><td>${pill('ok', 'Aplicado')}</td><td></td></tr>`,
    })),
  ]
    .sort((a, b) => a.on.localeCompare(b.on))
    .map((x) => x.html)
    .join('');
  const payments = paymentRows
    ? `<div class="table-wrap" data-component="table"><table class="table payments"><thead><tr><th>Fecha</th><th>Recibo</th><th class="num">Monto</th><th>Método</th><th>Referencia</th><th>Registró</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>${paymentRows}</tbody></table></div>`
    : `<p class="muted policy-note">Todavía no hay pagos${byAgency ? '' : ': la aseguradora cobra los recibos y Nubi los lee de ella'}.</p>`;
  const links = d.links.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Recibo</th><th class="num">Monto</th><th>Enviado</th><th>Vence</th><th>Estado</th></tr></thead><tbody>${d.links
        .map((l) => {
          const [label, tone] = LINK[l.state] ?? [l.state, 'info'];
          return `<tr><td class="mono">${l.installment}/${n}</td><td class="num">${fmt(l.amount)}</td><td class="nowrap">${esc(when(l.createdAt))}</td><td class="nowrap">${esc(when(l.expiresAt))}</td><td>${pill(tone, label)}</td></tr>`;
        })
        .join('')}</tbody></table></div>`
    : '';
  const balance = balanceOf(r);
  return `<div class="policy-grid">
    <section class="card"><div class="card-head"><h3>Prima anual</h3></div>${breakdown}</section>
    <section class="card"><div class="card-head"><h3>Forma de pago</h3></div>${dl([
      ['Forma de pago', frequency],
      ['Cobra', byAgency ? 'La agencia' : 'La aseguradora'],
      [
        'Saldo pendiente',
        `<strong class="policy-balance" data-policy-balance>${moneyList(balance, fmt({ amountMinor: 0, currency: p.currency === 'USD' ? 'USD' : 'GTQ' }))}</strong>`,
      ],
      [
        'Próximo recibo',
        next ? `${fmt(next.amount)} · vence el ${esc(date(next.dueDate))}` : 'Ninguno por cobrar',
      ],
    ])}</section></div>
    <section class="card"><div class="card-head"><h3>Recibos</h3><span class="spacer"></span><span class="muted small">${n} ${n === 1 ? 'recibo' : 'recibos'}</span></div>
      <div class="table-wrap" data-component="table"><table class="table receipts"><thead><tr><th>Recibo</th><th>Período</th><th>Vence</th><th class="num">Monto</th><th>Estado</th><th>Pagado el</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>${rows}</tbody></table></div></section>
    <section class="card"><div class="card-head"><h3>Pagos hechos</h3></div>${payments}</section>
    <div data-voucher-slot hidden></div>
    ${links ? `<section class="card"><div class="card-head"><h3>Enlaces de pago enviados</h3></div>${links}</section>` : ''}
    ${effortsHtml(d.efforts, r.installments, r.id)}`;
}

function commissionTab(c: PolicyCommission) {
  const t = c.term;
  const rows = c.accruals
    .map((a) => {
      const [label, tone] = STANDING[a.standing] ?? [a.standing, 'info'];
      return `<tr><td class="nowrap">${esc(date(a.date))}</td><td>${esc(ACCRUAL[a.kind] ?? a.kind)}</td><td class="mono">${a.installment ?? '—'}</td><td class="num">${fmt(a.base)}</td><td class="num">${fmt(a.commission)}</td><td>${pill(tone, label)}${a.disputed ? ' <span class="muted small">en disputa con la aseguradora</span>' : ''}</td></tr>`;
    })
    .join('');
  return `<section class="card"><div class="card-head"><h3>Comisión del período</h3></div>${
    t
      ? dl([
          ['Tasa', `<strong data-commission-rate>${esc(t.rate)} %</strong>`],
          ['Base (prima neta)', fmt(t.netPremium)],
          ['Comisión anual', `<strong data-commission-annual>${fmt(t.annualCommission)}</strong>`],
          [
            'Devengado hasta hoy',
            moneyList(c.accrued, fmt({ amountMinor: 0, currency: t.annualCommission.currency })),
          ],
        ])
      : '<p class="muted policy-note">Esta póliza no tiene un término de comisión registrado.</p>'
  }</section>
  <section class="card"><div class="card-head"><h3>Devengos de esta póliza</h3></div>${
    rows
      ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Fecha</th><th>Tipo</th><th>Recibo</th><th class="num">Base</th><th class="num">Comisión</th><th>Estado</th></tr></thead><tbody>${rows}</tbody></table></div>`
      : '<p class="muted policy-note">Todavía no hay comisiones devengadas: se devengan cuando la aseguradora recibe cada recibo.</p>'
  }</section>`;
}

/** The policy's history, oldest first: issuance, changes, renewal, cancellation, requests, notices. */
function historyTab(d: PolicyPageData) {
  const p = d.policy;
  type Event = { at: string; kind: [string, string]; note: string };
  const ev: Event[] = [];
  if (p.issuedOn)
    ev.push({
      at: p.issuedOn,
      kind: ['Emisión', 'ok'],
      note: `Póliza <b class="mono">${esc(p.policyNumber)}</b> emitida con ${esc(carrierName(p.carrier))}${planName(p) ? `, plan ${esc(planName(p))}` : ''}.`,
    });
  if (p.previousId)
    ev.push({
      at: p.period.start,
      kind: ['Renovación', 'info'],
      note: 'Esta vigencia renueva la anterior.',
    });
  for (const x of (p.riskHistory ?? []).slice(1))
    ev.push({
      at: x.at.slice(0, 10),
      kind: ['Endoso', 'info'],
      note: `Cambió el riesgo asegurado${x.reference ? ` (<span class="mono">${esc(x.reference)}</span>)` : ''}.`,
    });
  for (const x of (p.beneficiaryHistory ?? []).slice(1))
    ev.push({
      at: x.at.slice(0, 10),
      kind: ['Endoso', 'info'],
      note: `Cambiaron los beneficiarios${x.reference ? ` (<span class="mono">${esc(x.reference)}</span>)` : ''}.`,
    });
  for (const o of d.operations)
    ev.push({
      at: o.createdAt,
      kind: [o.kind === 'ISSUE' ? 'Solicitud de emisión' : 'Solicitud de endoso', 'warn'],
      note: `<span class="mono">${esc(o.carrierRef)}</span>${o.option ? ` · ${esc(o.option)}` : ''} · ${
        o.state === 'PENDING'
          ? 'en revisión de la aseguradora'
          : o.state === 'DONE'
            ? 'la aseguradora la aceptó'
            : `la aseguradora la rechazó${o.reason ? `: ${esc(o.reason)}` : ''}`
      }.`,
    });
  for (const n of d.notices)
    ev.push({
      at: n.sentAt,
      kind: ['Aviso', 'info'],
      note: `${n.kind === 'RENEWAL_POLICYHOLDER' ? 'Aviso de renovación al contratante' : 'Aviso de renovación al agente'} (${esc(n.window.replace(/^D-/, ''))} días antes).`,
    });
  if (p.renewedById && p.period.end)
    ev.push({
      at: p.period.end,
      kind: ['Renovada', 'ok'],
      note: 'La aseguradora renovó la póliza.',
    });
  if (p.cancellation)
    ev.push({
      at: p.cancellation.registeredOn ?? p.cancellation.effectiveDate ?? p.period.start,
      kind: ['Cancelación', 'bad'],
      note: `Cancelada${p.cancellation.effectiveDate ? ` desde el ${esc(date(p.cancellation.effectiveDate))}` : ''}${p.cancellation.reason ? `, ${esc(CANCEL_REASON[p.cancellation.reason] ?? p.cancellation.reason)}` : ''}.`,
    });
  if (p.lapsedOn)
    ev.push({
      at: p.lapsedOn,
      kind: ['Caducada', 'bad'],
      note: 'La aseguradora la reporta caducada.',
    });
  ev.sort((a, b) => a.at.localeCompare(b.at));
  // Its endorsements, its cancellation with the carrier's figures and the decided requests (#311).
  const changes = `<section class="card policy-history" aria-label="Endosos y cancelación"><div class="card-head"><h3>Endosos y cancelación</h3></div>${historyHtml(
    {
      policyNumber: p.policyNumber,
      endorsements: p.endorsements ?? [],
      cancellation: p.cancellation,
      cancellationRequests: p.cancellationRequests ?? [],
    },
  )}</section>`;
  if (!ev.length)
    return `<section class="card"><p class="muted policy-note">Todavía no hay movimientos de esta póliza.</p></section>${changes}`;
  return `${changes}<section class="card"><ol class="timeline">${ev
    .map(
      (e) =>
        `<li>${pill(e.kind[1], e.kind[0])}<span class="muted small">${esc(e.at.length > 10 ? when(e.at) : date(e.at))}</span><span class="timeline-note">${e.note}</span></li>`,
    )
    .join('')}</ol></section>`;
}

function documentsTab(d: PolicyPageData) {
  const p = d.policy;
  const docs = documentsOf(d);
  if (!has('agency_director', 'agency_admin', 'agency_manager', 'agent'))
    return '<section class="card"><p class="muted policy-note">Los documentos de la póliza los descargan la dirección, la administración, la gerencia y el agente de la póliza.</p></section>';
  if (!docs.length)
    return `<section class="card"><p class="muted policy-note">${esc(carrierName(p.carrier))} no entrega documentos de sus pólizas por sistema.</p></section>`;
  return `<section class="card"><div class="card-head"><h3>Documentos de la aseguradora</h3><span class="spacer"></span><span class="muted small">PDF de ${esc(carrierName(p.carrier))}</span></div>
    <div class="policy-docs">${docs
      .map(
        (k) =>
          `<button type="button" class="btn" data-policy-doc="${esc(p.scheduleId)}" data-kind="${esc(k)}" data-number="${esc(p.policyNumber)}" aria-label="Descargar ${esc(DOCUMENT[k]?.toLowerCase() ?? k)} de la póliza ${esc(p.policyNumber)} en PDF">${icon('download')}${esc(DOCUMENT[k] ?? k)}</button>`,
      )
      .join('')}</div></section>`;
}

/** The tabs this person sees for this policy (Comisión only when the commission was readable). */
export const visibleTabs = (d: Pick<PolicyPageData, 'commission' | 'claims'>) =>
  POLICY_TABS.filter(
    ([k]) =>
      (k !== 'comision' || d.commission !== null) &&
      (k !== 'siniestros' || (d.claims !== null && d.claims !== undefined)),
  );

/** The policy's page: header, actions, indicators and the tabs (the chosen one open). */
export function policyPageHtml(d: PolicyPageData, tab: PolicyTab) {
  const tabs = visibleTabs(d);
  const open = tabs.some(([k]) => k === tab) ? tab : 'resumen';
  const body: Record<PolicyTab, () => string> = {
    resumen: () => summaryTab(d),
    riesgo: () => riskTab(d),
    prima: () => premiumTab(d),
    comision: () => (d.commission ? commissionTab(d.commission) : ''),
    siniestros: () => policyClaimsHtml(d.policy.id, d.policy.policyNumber, d.claims ?? []),
    tareas: () =>
      followUpHtml(
        {
          tasks: d.policy.openTasks ?? [],
          notes: d.policy.notes ?? [],
          canNote: d.policy.canNote === true,
        },
        { kind: 'POLICY', id: d.policy.id },
      ),
    historial: () => historyTab(d),
    documentos: () => documentsTab(d),
  };
  return `<article class="policy-page" id="policy-page" data-policy="${esc(d.policy.scheduleId)}" data-policy-number="${esc(d.policy.policyNumber)}" tabindex="-1" aria-label="Póliza ${esc(d.policy.policyNumber)}">
  ${header(d)}
  <div class="policy-tabs" role="tablist" aria-label="Lo que tiene la póliza" data-policy-tabs>${tabs
    .map(
      ([k, label]) =>
        `<button type="button" class="policy-tab" role="tab" id="pt-${k}" aria-controls="pp-${k}" aria-selected="${k === open}" tabindex="${k === open ? 0 : -1}" data-policy-tab="${k}">${esc(label)}${k === 'siniestros' && d.claims?.length ? ` <span class="tab-count" aria-label="${d.claims.filter((c) => c.open).length} abiertos de ${d.claims.length}">${d.claims.filter((c) => c.open).length}/${d.claims.length}</span>` : ''}${k === 'tareas' && d.policy.openTasks?.length ? ` <span class="tab-count" aria-label="${d.policy.openTasks.length} tareas abiertas">${d.policy.openTasks.length}</span>` : ''}</button>`,
    )
    .join('')}</div>
  ${tabs
    .map(
      ([k]) =>
        `<div class="policy-panel" role="tabpanel" id="pp-${k}" aria-labelledby="pt-${k}" tabindex="0"${k === open ? '' : ' hidden'}>${body[k]()}</div>`,
    )
    .join('')}
  </article>`;
}

/**
 * Shows a tab of the page without asking the API again (every panel is already painted); the
 * tab bar keeps one tab in the Tab order (roving tabindex).
 */
export function selectTab(root: ParentNode, tab: string, focus = false): boolean {
  const button = root.querySelector<HTMLElement>(`[data-policy-tab="${CSS.escape(tab)}"]`);
  if (!button) return false;
  for (const b of root.querySelectorAll<HTMLElement>('[data-policy-tab]')) {
    const on = b === button;
    b.setAttribute('aria-selected', String(on));
    b.tabIndex = on ? 0 : -1;
    const panel = root.querySelector<HTMLElement>(`#${b.getAttribute('aria-controls')}`);
    if (panel) panel.hidden = !on;
  }
  if (focus) button.focus();
  return true;
}

/**
 * The keyboard of the tab bar (WAI-ARIA tabs): ←/→ move to the previous/next tab and show it,
 * Home and End to the first and last. `onSelect` hears which tab is shown.
 */
export function bindPolicyTabs(root: Document, onSelect: (tab: string) => void) {
  root.addEventListener('keydown', (ev) => {
    const e = ev as KeyboardEvent;
    const tab = (e.target as HTMLElement).closest?.<HTMLElement>('[data-policy-tab]');
    const bar = tab?.closest<HTMLElement>('[data-policy-tabs]');
    if (!tab || !bar) return;
    const all = [...bar.querySelectorAll<HTMLElement>('[data-policy-tab]')];
    const i = all.indexOf(tab);
    const to =
      e.key === 'ArrowRight'
        ? all[(i + 1) % all.length]
        : e.key === 'ArrowLeft'
          ? all[(i - 1 + all.length) % all.length]
          : e.key === 'Home'
            ? all[0]
            : e.key === 'End'
              ? all[all.length - 1]
              : null;
    if (!to?.dataset.policyTab) return;
    e.preventDefault();
    selectTab(root, to.dataset.policyTab, true);
    onSelect(to.dataset.policyTab);
  });
}

/** A policy that is not there (or not the person's) says so, with the way back. */
export const policyMissing = (why: string) =>
  `${pageHead('Póliza', 'No se pudo abrir esta póliza.', '<button type="button" class="btn" data-policy-back>← Pólizas y recibos</button>')}${empty('policy', 'Esta póliza no está disponible', esc(why), '<button type="button" class="btn primary" data-policy-back>Volver al listado</button>', 2)}`;
